/**
 * 🎨 MUI Component Overrides - Modern Design System 2025
 * 
 * Surcharge les styles par défaut des composants Material-UI
 * pour un look moderne et cohérent + RESPONSIVE MOBILE
 */

export const getMuiOverrides = (theme) => ({
  // 🔘 BUTTONS - Style moderne avec gradients + Mobile responsive
  MuiButton: {
    root: {
      borderRadius: 8,
      textTransform: 'none',
      fontWeight: 500,
      padding: '8px 20px',
      boxShadow: 'none',
      transition: 'all 0.2s ease-in-out',
      
      '&:hover': {
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
      },
      
      // Mobile: Taille tactile minimum iOS (44x44px)
      '@media (max-width: 599.95px)': {
        padding: '10px 16px',
        fontSize: '0.875rem',
        minWidth: '44px',
        minHeight: '44px',
      },
    },
    contained: {
      boxShadow: 'none',
      '&:hover': {
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
      },
    },
    containedPrimary: {
      background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
      '&:hover': {
        background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
      },
    },
    containedSecondary: {
      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      '&:hover': {
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      },
    },
    outlined: {
      borderWidth: 1.5,
      '&:hover': {
        borderWidth: 1.5,
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
      },
    },
  },

  // 📄 PAPER/CARDS - Moderne avec borders subtiles
  MuiPaper: {
    root: {
      backgroundImage: 'none',
    },
    rounded: {
      borderRadius: 12,
      '@media (max-width: 599.95px)': {
        borderRadius: 8,
      },
    },
    outlined: {
      border: '1px solid rgba(148, 163, 184, 0.12)',
    },
    elevation1: {
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.4)',
    },
    elevation2: {
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
    },
    elevation3: {
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.4)',
    },
  },

  // 📦 CARDS - Headers modernisés + Mobile
  MuiCard: {
    root: {
      backgroundImage: 'none',
      borderRadius: 12,
      border: '1px solid rgba(148, 163, 184, 0.12)',
      '@media (max-width: 599.95px)': {
        borderRadius: 8,
      },
    },
  },
  MuiCardHeader: {
    root: {
      borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
      background: 'rgba(59, 130, 246, 0.03)',
      '@media (max-width: 599.95px)': {
        padding: '12px 16px',
      },
    },
    title: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
      fontSize: '1.125rem',
      '@media (max-width: 599.95px)': {
        fontSize: '1rem',
      },
    },
  },
  MuiCardContent: {
    root: {
      '@media (max-width: 599.95px)': {
        padding: '12px 16px',
        '&:last-child': {
          paddingBottom: '16px',
        },
      },
    },
  },

  // 🏷️ CHIPS - Modernes avec glass effect + Mobile
  MuiChip: {
    root: {
      borderRadius: 8,
      fontWeight: 500,
      height: 28,
      fontSize: '0.875rem',
      transition: 'all 0.2s ease-in-out',
      '@media (max-width: 599.95px)': {
        height: 24,
        fontSize: '0.75rem',
      },
    },
    colorPrimary: {
      backgroundColor: 'rgba(59, 130, 246, 0.15)',
      color: '#60A5FA',
      border: '1px solid rgba(59, 130, 246, 0.3)',
    },
    colorSecondary: {
      backgroundColor: 'rgba(16, 185, 129, 0.15)',
      color: '#34D399',
      border: '1px solid rgba(16, 185, 129, 0.3)',
    },
  },

  // 📝 TEXT FIELDS - Inputs modernes + Mobile responsive
  MuiTextField: {
    root: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 8,
        transition: 'all 0.2s ease-in-out',
        
        '&:hover': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.light,
          },
        },
        
        '&.Mui-focused': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
            borderColor: theme.palette.primary.main,
          },
        },
      },
    },
  },
  MuiOutlinedInput: {
    root: {
      '@media (max-width: 599.95px)': {
        fontSize: '16px', // Évite le zoom iOS
      },
    },
    input: {
      padding: '14px 16px',
      '@media (max-width: 599.95px)': {
        padding: '12px 14px',
      },
    },
    inputMarginDense: {
      paddingTop: 10,
      paddingBottom: 10,
    },
  },
  MuiInputLabel: {
    outlined: {
      transform: 'translate(16px, 16px) scale(1)',
      '&$marginDense': {
        transform: 'translate(16px, 12px) scale(1)',
      },
      '@media (max-width: 599.95px)': {
        fontSize: '0.875rem',
      },
    },
  },
  MuiFormControl: {
    root: {
      '@media (max-width: 599.95px)': {
        marginBottom: '12px',
        width: '100%',
      },
    },
  },

  // 🔲 CHECKBOXES & SWITCHES - Modernes + Mobile
  MuiCheckbox: {
    root: {
      borderRadius: 4,
      '&:hover': {
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
      },
      '@media (max-width: 599.95px)': {
        padding: '12px', // Zone tactile plus grande
      },
    },
  },
  MuiSwitch: {
    root: {
      width: 42,
      height: 26,
      padding: 0,
      '@media (max-width: 599.95px)': {
        width: 48,
        height: 30,
      },
    },
    switchBase: {
      padding: 1,
      '&$checked': {
        transform: 'translateX(16px)',
        '& + $track': {
          backgroundColor: theme.palette.primary.main,
          opacity: 1,
        },
      },
      '@media (max-width: 599.95px)': {
        '&$checked': {
          transform: 'translateX(18px)',
        },
      },
    },
    thumb: {
      width: 24,
      height: 24,
      boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.4)',
      '@media (max-width: 599.95px)': {
        width: 28,
        height: 28,
      },
    },
    track: {
      borderRadius: 13,
      opacity: 1,
      backgroundColor: 'rgba(148, 163, 184, 0.3)',
      '@media (max-width: 599.95px)': {
        borderRadius: 15,
      },
    },
  },

  // 📊 TABLES - Design moderne + Mobile responsive
  MuiTableCell: {
    root: {
      borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
      padding: '16px 20px',
      fontSize: '0.938rem',
      '@media (max-width: 599.95px)': {
        padding: '10px 8px',
        fontSize: '0.75rem',
      },
    },
    head: {
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.05em',
      backgroundColor: 'rgba(59, 130, 246, 0.03)',
      padding: '14px 20px',
      '@media (max-width: 599.95px)': {
        padding: '8px 8px',
        fontSize: '0.625rem',
      },
    },
    sizeSmall: {
      padding: '10px 16px',
      '&$head': {
        padding: '10px 16px',
      },
      '@media (max-width: 599.95px)': {
        padding: '6px 4px',
        '&$head': {
          padding: '6px 4px',
        },
      },
    },
  },
  MuiTableRow: {
    root: {
      transition: 'background-color 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: 'rgba(59, 130, 246, 0.04)',
      },
    },
  },
  MuiTable: {
    root: {
      '@media (max-width: 599.95px)': {
        // Permettre scroll horizontal sur mobile
        display: 'block',
        overflowX: 'auto',
        '-webkit-overflow-scrolling': 'touch',
      },
    },
  },

  // 📋 DIALOGS - Modernes avec backdrop blur + Mobile responsive
  MuiDialog: {
    paper: {
      borderRadius: 16,
      backgroundImage: 'none',
      '@media (max-width: 599.95px)': {
        margin: 8,
        width: 'calc(100% - 16px)',
        maxHeight: 'calc(100% - 16px)',
        borderRadius: 12,
      },
    },
  },
  MuiDialogTitle: {
    root: {
      fontSize: '1.25rem',
      fontWeight: 600,
      borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
      padding: '20px 24px',
      '@media (max-width: 599.95px)': {
        fontSize: '1.125rem',
        padding: '16px 20px',
      },
    },
  },
  MuiDialogContent: {
    root: {
      padding: '24px',
      '@media (max-width: 599.95px)': {
        padding: '20px 16px',
      },
    },
  },
  MuiDialogActions: {
    root: {
      padding: '16px 24px',
      borderTop: '1px solid rgba(148, 163, 184, 0.12)',
      '@media (max-width: 599.95px)': {
        padding: '12px 16px',
        flexDirection: 'column',
        '& > *': {
          width: '100%',
          margin: '4px 0 !important',
        },
      },
    },
  },

  // 🎯 TABS - Modernes + Mobile
  MuiTabs: {
    root: {
      borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
      '@media (max-width: 599.95px)': {
        // Tabs scrollables sur mobile
        '& .MuiTabs-flexContainer': {
          overflowX: 'auto',
          '-webkit-overflow-scrolling': 'touch',
        },
      },
    },
    indicator: {
      height: 3,
      borderRadius: '3px 3px 0 0',
      background: 'linear-gradient(90deg, #3B82F6 0%, #2563EB 100%)',
    },
    scrollButtons: {
      '@media (max-width: 599.95px)': {
        width: 32,
      },
    },
  },
  MuiTab: {
    root: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.938rem',
      minHeight: 48,
      transition: 'all 0.2s ease-in-out',
      
      '&:hover': {
        color: theme.palette.primary.light,
        backgroundColor: 'rgba(59, 130, 246, 0.04)',
      },
      
      '&$selected': {
        color: theme.palette.primary.main,
        fontWeight: 600,
      },
      
      '@media (max-width: 599.95px)': {
        fontSize: '0.813rem',
        minHeight: 44,
        minWidth: 90,
        padding: '6px 12px',
      },
    },
  },

  // 📌 TOOLTIPS - Modernes + Mobile
  MuiTooltip: {
    tooltip: {
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      backdropFilter: 'blur(8px)',
      borderRadius: 8,
      padding: '8px 12px',
      fontSize: '0.813rem',
      border: '1px solid rgba(148, 163, 184, 0.12)',
      '@media (max-width: 599.95px)': {
        fontSize: '0.75rem',
        padding: '6px 10px',
      },
    },
    arrow: {
      color: 'rgba(15, 23, 42, 0.95)',
    },
  },

  // 🔔 ALERTS - Modernes avec icônes colorées + Mobile
  MuiAlert: {
    root: {
      borderRadius: 8,
      backdropFilter: 'blur(8px)',
      '@media (max-width: 599.95px)': {
        padding: '8px 12px',
        fontSize: '0.813rem',
      },
    },
    standardError: {
      backgroundColor: 'rgba(239, 68, 68, 0.15)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      color: '#F87171',
    },
    standardWarning: {
      backgroundColor: 'rgba(245, 158, 11, 0.15)',
      border: '1px solid rgba(245, 158, 11, 0.3)',
      color: '#FBBF24',
    },
    standardInfo: {
      backgroundColor: 'rgba(59, 130, 246, 0.15)',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      color: '#60A5FA',
    },
    standardSuccess: {
      backgroundColor: 'rgba(16, 185, 129, 0.15)',
      border: '1px solid rgba(16, 185, 129, 0.3)',
      color: '#34D399',
    },
  },

  // 🍔 APP BAR - Mobile responsive
  MuiAppBar: {
    root: {
      '@media (max-width: 599.95px)': {
        '& .MuiToolbar-root': {
          minHeight: 56,
          paddingLeft: 16,
          paddingRight: 16,
        },
      },
    },
  },

  // 📱 DRAWER - Mobile responsive
  MuiDrawer: {
    paper: {
      '@media (max-width: 599.95px)': {
        width: '85%',
        maxWidth: 320,
      },
    },
  },

  // 🎛️ SELECT - Mobile responsive
  MuiSelect: {
    root: {
      '@media (max-width: 599.95px)': {
        fontSize: '16px', // Évite le zoom iOS
      },
    },
  },

  // 📄 LIST - Mobile responsive
  MuiListItem: {
    root: {
      '@media (max-width: 599.95px)': {
        paddingLeft: 16,
        paddingRight: 16,
        minHeight: 48, // Taille tactile
      },
    },
  },
  MuiListItemText: {
    primary: {
      '@media (max-width: 599.95px)': {
        fontSize: '0.938rem',
      },
    },
    secondary: {
      '@media (max-width: 599.95px)': {
        fontSize: '0.813rem',
      },
    },
  },
})
